"use client"

import * as React from "react"

import {
  getStorageItem,
  setStorageItem,
  subscribeToStorageKey,
} from "@exxatdesignux/ui/lib/persisted-state"

import { productPersistKey } from "@/stores/app-store"

export type EvaluationDashboardApproach =
  | "lifecycle"
  | "action"
  | "analytic"
  | "quality"
  | "operations"
  | "operations-v2"

export const EVALUATION_DASHBOARD_APPROACHES: {
  id: EvaluationDashboardApproach
  label: string
  description: string
}[] = [
  {
    id: "lifecycle",
    label: "Lifecycle",
    description: "Current and last term cards, then past analysis",
  },
  {
    id: "action",
    label: "Action queue",
    description: "Prioritized work list with next steps",
  },
  {
    id: "analytic",
    label: "Analytics",
    description: "Closed term summary, trends, and exportable history",
  },
  {
    id: "quality",
    label: "Education quality",
    description: "Health score, learner voice, and where action is needed",
  },
  {
    id: "operations",
    label: "Operations",
    description: "All active terms, collection stages, and coordinator actions",
  },
  {
    id: "operations-v2",
    label: "Operations V2",
    description: "Needs-attention queue, live term, then compressed other terms",
  },
]

const STORAGE_KEY = productPersistKey("exxat-surveys", "dashboard-approach")
const VERSION = 1
const DEFAULT_APPROACH: EvaluationDashboardApproach = "lifecycle"

const listeners = new Set<() => void>()
let snapshot: EvaluationDashboardApproach | null = null
let hydrated = false

function isApproach(value: unknown): value is EvaluationDashboardApproach {
  return (
    value === "lifecycle" ||
    value === "action" ||
    value === "analytic" ||
    value === "quality" ||
    value === "operations" ||
    value === "operations-v2"
  )
}

function subscribe(onChange: () => void) {
  listeners.add(onChange)
  const unsubscribeStorage = subscribeToStorageKey(STORAGE_KEY, () => {
    hydrated = false
    onChange()
  })
  return () => {
    listeners.delete(onChange)
    unsubscribeStorage()
  }
}

function read(): EvaluationDashboardApproach {
  if (hydrated && snapshot) return snapshot
  hydrated = true

  const raw = getStorageItem(STORAGE_KEY)
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw)
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        (parsed as { v?: number }).v === VERSION &&
        isApproach((parsed as { d?: unknown }).d)
      ) {
        snapshot = (parsed as { d: EvaluationDashboardApproach }).d
        return snapshot
      }
    } catch {
      // fall through
    }
  }

  snapshot = DEFAULT_APPROACH
  return snapshot
}

export function setEvaluationDashboardApproach(next: EvaluationDashboardApproach) {
  snapshot = next
  hydrated = true
  setStorageItem(STORAGE_KEY, JSON.stringify({ v: VERSION, d: next }))
  for (const listener of listeners) listener()
}

export function useEvaluationDashboardApproach(): EvaluationDashboardApproach {
  return React.useSyncExternalStore(subscribe, read, () => DEFAULT_APPROACH)
}
